import React, { useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useRef, useState } from 'react';
import PwChangePopup from '../pages/System/Popup/PWChangePopup';
import FavoritePopup from '../pages/System/Popup/FavoritePopup';
import AlarmPopup from '../pages/System/Popup/AlarmPopup';
import { PAGE_RENDER_TYPE } from 'e3-common/constants/enumerates';
import styles from './TopMenu.module.css';
import useFetch from 'e3-common/hooks/useFetch';
import useAuth from 'e3-common/hooks/useAuth';
import { toggleFullScreen } from 'e3-common/utils/uiUtil';
import { useBoundStore } from 'e3-common/store/useBoundStore';
import { LOCAL_STORAGE_KEYS } from 'e3-common/constants/enumerates';
import { setLocalStorageItem } from 'e3-common/utils/localStorageUtil';
import useTabMenu from 'e3-common/hooks/useTabMenu';
import { shallow } from 'zustand/shallow';
import { HOME_MENU_ID } from 'e3-common/constants/constMenuId';
import { THEME } from 'e3-common/constants/common';
import _ from 'lodash';

const TopMenu = () => {
    const { logout } = useAuth();
    const { request } = useFetch();
    const { changeTab } = useTabMenu();
    const iconRef = useRef();
    const [sseData, setSseData] = useState([]);
    const [alarmData, setAlarmData] = useState([]);

    const navigate = useNavigate();
    const menuList = useBoundStore((state) => state.menu.menuList);

    const {
        user,
        hierarchyMenu,
        pageRenderType,
        showTopMenuYn,
        selectedTopMenu,
        setSelectedTopMenu,
        setSelectedMenu,
    } = useBoundStore(
        (state) => ({
            user: state.user,
            hierarchyMenu: state.menu.hierarchyMenu,
            pageRenderType: state.menu.pageRenderType,
            showTopMenuYn: state.menu.showTopMenuYn,
            selectedTopMenu: state.menu.selectedTopMenu,
            setSelectedTopMenu: state.menu.setSelectedTopMenu,
            setSelectedMenu: state.menu.setSelectedMenu,
            helpMenuYn: state.menu.helpMenuYn,
        }),
        shallow
    );

    const [fullScreenMode, setFullScreenMode] = useState(false);

    const controls = {
        pwChangePopup: useRef(),
        FavoritePopup: useRef(),
        AlarmPopup: useRef(),
    };

    const eventHandler = {
        button: {
            onLoginClick: () => {
                navigate(import.meta.env.VITE_PUBLIC_URL + '/login');
            },
            onLogoutClick: async () => {
                logout(user?.userId);
            },
            onFullScreenClick: () => {
                if (toggleFullScreen() === true) {
                    setFullScreenMode(true);
                } else {
                    setFullScreenMode(false);
                }
            },
            onLightClick: (color) => {
                const style = 'light ' + color;

                eventHandler.themeChg(style);
                document.body.classList = [style];
            },
            onDarkClick: (color) => {
                const style = 'dark ' + color;

                eventHandler.themeChg(style);
                document.body.classList = [style];
            },
        },
        onMenuClick: (m) => {
            if (!m) {
                setSelectedTopMenu({
                    menuId: '',
                    menuName: '',
                    sideYn: 'N',
                    displayYn: 'N',
                    menuNameList: '',
                });
                return;
            }

            setSelectedTopMenu({
                menuId: m.menuId,
                menuName: m.menuName,
                sideYn: m.sideYn,
                displayYn: m.displayYn,
                menuNameList: m.menuNameList,
            });
            if (pageRenderType === PAGE_RENDER_TYPE.PAGE && m.programRoutePath) {
                navigate(import.meta.env.VITE_PUBLIC_URL + m.programRoutePath);
                return;
            }
        },
        setHome: (m) => {
            // 그리드 개인화 기능을 위한 메뉴ID 인식
            setLocalStorageItem(LOCAL_STORAGE_KEYS.MENU_ID, m);

            setSelectedMenu({
                menuId: m,
                menuName: 'home',
                sideYn: 'Y',
                displayYn: 'Y',
                menuNameList: 'home',
            });

            changeTab(m);
        },
        themeChg: (color) => {
            if (user?.userId) {
                const param = { USER_ID: user?.userId, PROP_STR1: color };
                request(
                    `/E3/SYSTEM/ChangeTheme`,
                    { method: 'POST', body: param },
                    { showConfirm: false }
                );
            }
        },
        onShowPopup: {
            pwChange: () => {
                controls.pwChangePopup.current?.showPopup();
            },
            favorite: () => {
                controls.FavoritePopup.current?.openPopup(menuList);
            },
            alarm: () => {
                const rect = iconRef.current.getBoundingClientRect();

                const position = {
                    left: rect.left + window.scrollX,
                    top: rect.top + rect.height + window.scrollY,
                };
                afterProcessing();
                controls.AlarmPopup.current?.openPopup(alarmData.length ?? 0, position);
            },
        },
        sse: {
            afterProcessing: (data, e) => {
                request(
                    `/E3/SYSTEM/GetAlarmList`,
                    { method: 'POST', body: param },
                    { showConfirm: false, showLoad: false }
                );
            },
            updateReadYn: (e) => {
                const response = request(
                    `/E3/SYSTEM/UpdateReadYn`,
                    { method: 'POST', body: { READ_YN: 'Y' } },
                    { showConfirm: false, showLoad: false }
                );
                if (response) {
                    afterProcessing();
                }
            },
        },
    };

    const afterProcessing = async () => {
        const result = await request(
            `/E3/SYSTEM/GetAlarmList`,
            { method: 'POST', body: {} },
            { showConfirm: false, showLoad: false }
        );
        if (result) {
            controls.AlarmPopup.current?.bindingGrid(result, () => setAlarmData(result));
        }
    };

    useEffect(() => {
        afterProcessing();
    }, [sseData]);

    return (
        // <!-- header -->
        <div className={styles.header}>
            <div className={styles.headerWeb}>
                <div className={styles.headerLogo}>
                    <Link
                        to={import.meta.env.VITE_PUBLIC_URL + '/vct/ogm010'}
                        onClick={() => eventHandler.setHome(HOME_MENU_ID)}
                    >
                        <img
                            src={
                                import.meta.env.VITE_PUBLIC_URL +
                                '/assets/images/logo/insighton/Insighton_logo_top.png'
                            }
                            alt="Logo"
                        />
                    </Link>
                </div>
                <div className={styles.headerGnb}>
                    {showTopMenuYn === 'Y' ? (
                        <ul>
                            {hierarchyMenu.map((m) => (
                                <li key={m.menuId} onClick={() => eventHandler.onMenuClick(m)}>
                                    <div
                                        className={
                                            selectedTopMenu?.menuId === m.menuId
                                                ? styles.selected
                                                : ''
                                        }
                                    >
                                        {m.menuName}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : null}
                </div>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div className={styles.headerUserInfo}>
                        {/* 로그아웃 : 로그인 정보 있을때 */}
                        {user?.userId && (
                            <>
                                <span className={styles.headerPersonPhoto}></span>
                                <span
                                    className={styles.headerPerson}
                                    onClick={eventHandler.onShowPopup.pwChange}
                                >
                                    {user?.userName} ({user?.userId})
                                    <em className={styles.tooltip}>비밀번호 변경</em>
                                </span>{' '}
                                님
                                {/* <Link to={'/digital-signage/auto'}>
                  <span className={styles.userScreenGo}>
                    <button>Digital Signage Sample 보기</button>
                  </span>
                </Link>
                <span className={styles.divBar}></span> */}
                            </>
                        )}
                        {/* 로그인 : 로그인 정보 없을 때 */}
                        {!user?.userId && (
                            <button
                                className={`btn ${styles.headerLogin}`}
                                onClick={eventHandler.button.onLoginClick}
                            >
                                로그인
                            </button>
                        )}
                    </div>
                    <div
                        className={styles.divAlarm}
                        onClick={eventHandler.onShowPopup.alarm}
                        ref={iconRef}
                    >
                        {alarmData?.length ? (
                            <span>{alarmData?.length > 99 ? '99+' : alarmData?.length}</span>
                        ) : null}
                        <em className={styles.tooltip}>My Alarm</em>
                    </div>
                    <button
                        className={styles.btnFavorite}
                        onClick={eventHandler.onShowPopup.favorite}
                    >
                        <em className={styles.tooltip}>My Favorite</em>
                    </button>

                    <div className={styles.colormode}>
                        <a href="#" className={`${styles.colorLink} material-symbols-outlined`}>
                            water_drop
                        </a>
                        <ul className={styles.modeoption}>
                            <li>
                                <dl>
                                    <dt>Type</dt>
                                    <dd>
                                        <a
                                            onClick={() =>
                                                eventHandler.button.onLightClick(THEME.RED)
                                            }
                                            className={`${styles.subcolorLink} material-symbols-outlined red`}
                                        >
                                            humidity_high
                                        </a>
                                    </dd>
                                    <dd>
                                        <a
                                            onClick={() =>
                                                eventHandler.button.onLightClick(THEME.ORANGE)
                                            }
                                            className={`${styles.subcolorLink} material-symbols-outlined orange`}
                                        >
                                            humidity_high
                                        </a>
                                    </dd>
                                    <dd>
                                        <a
                                            onClick={() =>
                                                eventHandler.button.onLightClick(THEME.BLUE)
                                            }
                                            className={`${styles.subcolorLink} material-symbols-outlined blue`}
                                        >
                                            humidity_high
                                        </a>
                                    </dd>
                                    <dd>
                                        <a
                                            onClick={() =>
                                                eventHandler.button.onLightClick(THEME.GREEN)
                                            }
                                            className={`${styles.subcolorLink} material-symbols-outlined green`}
                                        >
                                            humidity_high
                                        </a>
                                    </dd>
                                </dl>
                            </li>
                        </ul>
                    </div>

                    <button
                        className={styles.btnLogout}
                        onClick={eventHandler.button.onLogoutClick}
                    >
                        <em className={styles.tooltip}>Logout</em>
                    </button>

                    <div className={styles.fullscreenIcon}>
                        {fullScreenMode === false ? (
                            <button
                                className={styles.iconFullscreen}
                                onClick={eventHandler.button.onFullScreenClick}
                            >
                                <em className={styles.tooltip}>Full Screen</em>
                            </button>
                        ) : (
                            <button
                                className={styles.iconOriginalscreen}
                                onClick={eventHandler.button.onFullScreenClick}
                            >
                                <em className={styles.tooltip}>Origin Screen</em>
                            </button>
                        )}
                    </div>
                    {/* ------------------------------- */}
                    {/* ----------- SSE 임시 ----------- */}
                    {/* <div>
            <button
              onClick={() => {
                fetchSSE(eventHandler.sse.afterProcessing);
              }}
            >
              SSE
            </button>
          </div> */}
                    {/* ----------- SSE 임시 ----------- */}
                    {/* ------------------------------- */}
                </div>
            </div>

            <PwChangePopup ref={controls.pwChangePopup} expiredCheck={user.expiredCheck} />
            <FavoritePopup ref={controls.FavoritePopup} />
            <AlarmPopup
                ref={controls.AlarmPopup}
                callBackAction={(e) => setSseData(e)}
                updateCallBack={(e) => eventHandler.sse.updateReadYn(e)}
            />
        </div>
    );
};

TopMenu.displayName = 'TopMenu';
export default TopMenu;
